import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, ArrowUpRight } from "lucide-react";
import { BUSINESS, NAV_LINKS, LOGO } from "../../lib/site";
import api from "../../lib/api";

export function OpeningHours({ dark = false }) {
  const [days, setDays] = useState(undefined);

  useEffect(() => {
    api.get("/settings/hours").then((res) => setDays(res.data.days)).catch(() => setDays(null));
  }, []);

  if (days === undefined) return null;

  if (!days || days.length === 0) {
    return (
      <p data-testid="opening-hours-placeholder" className={`text-sm ${dark ? "text-pale/60" : "text-muted-foreground"}`}>
        Opening hours are being confirmed. Please call{" "}
        <a href={BUSINESS.phoneHref} className="text-sky font-semibold">{BUSINESS.phone}</a> to arrange a time.
      </p>
    );
  }

  return (
    <ul data-testid="opening-hours-list" className="space-y-1.5 text-sm">
      {days.map((d) => (
        <li key={d.day} className="flex justify-between gap-6">
          <span className={dark ? "text-pale/70" : "text-muted-foreground"}>{d.day}</span>
          <span className={`font-mono text-[13px] ${dark ? "text-white" : "text-ink"}`}>
            {d.closed ? "Closed" : `${d.open} - ${d.close}`}
          </span>
        </li>
      ))}
    </ul>
  );
}

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="bg-navy text-pale">
      <div className="border-t border-deep/60">
        <div className="container-pr7 py-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <p className="font-heading font-bold text-white">Looking for genuine used Ford parts?</p>
            <p className="text-sm text-pale/60 mt-1">Visit our sister business, Blue Oval Spares / FordBits.</p>
          </div>
          <a
            href="https://www.fordbits.co.uk"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="sister-business-link"
            className="inline-flex items-center gap-2 border border-sky/40 text-sky hover:text-white hover:border-sky font-heading font-bold text-sm tracking-wide px-5 py-2.5 rounded transition-colors"
          >
            VISIT BLUE OVAL SPARES <ArrowUpRight size={16} />
          </a>
        </div>
      </div>

      <div className="border-t border-deep/60">
        <div className="container-pr7 py-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <img src={LOGO} alt="PR7 MOT Centre" className="h-11 w-auto rounded-sm" />
              <span className="font-heading font-extrabold text-white tracking-wide">MOT CENTRE</span>
            </div>
            <p className="text-sm text-pale/60 leading-relaxed">
              Professional MOT testing, servicing, inspections and vehicle maintenance from our workshop in Adlington, Lancashire.
            </p>
          </div>
          <div>
            <h4 className="font-heading font-bold text-white text-sm tracking-wider uppercase mb-4">Services</h4>
            <ul className="space-y-2 text-sm">
              {NAV_LINKS.slice(1, 6).map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-pale/60 hover:text-sky transition-colors">{l.label}</Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="font-heading font-bold text-white text-sm tracking-wider uppercase mb-4">Contact</h4>
            <ul className="space-y-3 text-sm">
              <li className="flex gap-2.5 text-pale/70">
                <MapPin size={16} className="text-sky shrink-0 mt-0.5" />
                <span>{BUSINESS.address}</span>
              </li>
              <li>
                <a href={BUSINESS.phoneHref} data-testid="footer-phone" className="flex gap-2.5 text-pale/70 hover:text-sky transition-colors">
                  <Phone size={16} className="text-sky shrink-0 mt-0.5" /> {BUSINESS.phone}
                </a>
              </li>
              <li>
                <a href={`mailto:${BUSINESS.email}`} data-testid="footer-email" className="flex gap-2.5 text-pale/70 hover:text-sky transition-colors break-all">
                  <Mail size={16} className="text-sky shrink-0 mt-0.5" /> {BUSINESS.email}
                </a>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="font-heading font-bold text-white text-sm tracking-wider uppercase mb-4">Opening Hours</h4>
            <OpeningHours dark />
          </div>
        </div>
      </div>

      <div className="border-t border-deep/60">
        <div className="container-pr7 py-5 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-pale/40">
          <p>&copy; {new Date().getFullYear()} PR7 MOT Centre, Adlington, Lancashire. All rights reserved.</p>
          <Link to="/admin" data-testid="footer-admin-link" className="hover:text-sky transition-colors">Admin Login</Link>
        </div>
      </div>
      <div className="h-16 md:hidden" />
    </footer>
  );
}
